import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useSkillLintMutation } from '@/modules/jobs/mutations/useSkillLintMutation';
import { useTitleLintMutation } from '@/modules/jobs/mutations/useTitleLintMutation';
import { useJobSignalQuery } from '@/modules/jobs/queries/useJobSignalQuery';

import { deriveNeeds } from './derivations';
import { normalize } from './displayCasing';
import type {
  JobSignal,
  RoleDraft,
  SkillVerdict,
  SufficiencyLevel,
  TitleVerdict,
} from './types';

/**
 * The live signal for a role draft: skill verdicts (`/ai/skill-lint`), the title verdict (`/ai/title-lint`) and
 * the completeness score (`/ai/job-signal`), plus the derived "still needed" list.
 *
 * This is the same logic `JobSignalPanel` keeps inline (its verdict/title caches, batching and lint-on-load).
 * The customer editor (`HMJobEditor`) uses this hook; the recruiter panel still has its own copy and should
 * migrate here next so there is one implementation. Keep the two in sync until then.
 */

type SkillLintStatus = 'pending' | 'failed';

const SKILL_LINT_BATCH_SIZE = 50;
/** Lint the title once typing pauses, so the hint is on screen before the user moves to the next field. */
const TITLE_LINT_IDLE_MS = 800;
export const SKILL_LINT_MAX_ITEM_LENGTH = 200;

export const isSkillLintTermTooLong = (skill: string): boolean =>
  Array.from(skill).length > SKILL_LINT_MAX_ITEM_LENGTH;

const byInput = (verdicts: SkillVerdict[]): Record<string, SkillVerdict> =>
  Object.fromEntries(verdicts.map((v) => [normalize(v.input), v]));

interface UseRoleSignalOptions {
  role: RoleDraft;
  /** Off in stories with no backend: no lint-on-load, no requests. */
  enabled?: boolean;
  /** Seeds for stories (production fills these from the endpoints). */
  initialVerdicts?: SkillVerdict[];
  initialTitleVerdict?: TitleVerdict;
  initialJobSignal?: JobSignal;
}

export function useRoleSignal({
  role,
  enabled = true,
  initialVerdicts = [],
  initialTitleVerdict,
  initialJobSignal,
}: UseRoleSignalOptions) {
  const [verdictCache, setVerdictCache] = useState<
    Record<string, SkillVerdict>
  >(() => byInput(initialVerdicts));
  const [skillLintStatus, setSkillLintStatus] = useState<
    Record<string, SkillLintStatus>
  >({});
  const [titleVerdictCache, setTitleVerdictCache] = useState<
    Record<string, TitleVerdict>
  >(() =>
    initialTitleVerdict && role.title
      ? { [normalize(role.title)]: initialTitleVerdict }
      : {},
  );
  const lint = useSkillLintMutation();
  const titleLint = useTitleLintMutation();
  // titles with an in-flight lint: repeat blurs of the same title must not bill a second LLM call
  const inFlightTitles = useRef<Set<string>>(new Set());

  const verdicts = useMemo(
    () =>
      role.mustHave
        .map((s) => verdictCache[normalize(s)])
        .filter((v): v is SkillVerdict => Boolean(v)),
    [role.mustHave, verdictCache],
  );
  const pendingCount = role.mustHave.filter(
    (skill) => skillLintStatus[normalize(skill)] === 'pending',
  ).length;
  const failedSkills = role.mustHave.filter(
    (skill) => skillLintStatus[normalize(skill)] === 'failed',
  );
  const titleVerdict = titleVerdictCache[normalize(role.title)] ?? null;
  const needs = useMemo(
    () => deriveNeeds(role, titleVerdict),
    [role, titleVerdict],
  );

  const jobSignalInputs = useMemo(
    () => ({
      title: role.title,
      titleVerdict,
      // The job-signal wire key is still city, but its heuristic only checks whether a place was entered.
      city: role.location,
      seniority: role.seniority,
      skillVerdicts: verdicts.map((v) => v.verdict),
    }),
    [role.title, titleVerdict, role.location, role.seniority, verdicts],
  );
  const jobSignalQuery = useJobSignalQuery(jobSignalInputs, initialJobSignal);
  const sufficiencyLevel: SufficiencyLevel =
    jobSignalQuery.data?.skillSufficiency.level ?? 'none';

  /** Lint only the skills without a verdict yet (on skill-commit, never per keystroke). */
  const lintSkills = (skills: string[]) => {
    if (!enabled) return;
    const seen = new Set<string>();
    const fresh = skills
      .map((skill) => skill.trim())
      .filter((skill) => {
        const key = normalize(skill);
        if (
          !skill ||
          seen.has(key) ||
          verdictCache[key] ||
          skillLintStatus[key] === 'pending'
        ) {
          return false;
        }
        seen.add(key);
        return true;
      });
    if (fresh.length === 0) return;

    const tooLong = fresh.filter(isSkillLintTermTooLong);
    if (tooLong.length > 0) {
      setVerdictCache((cache) => ({
        ...cache,
        ...byInput(
          tooLong.map((input) => ({
            input,
            canonical: input,
            verdict: 'unknown',
          })),
        ),
      }));
    }

    const lintable = fresh.filter((skill) => !isSkillLintTermTooLong(skill));
    const lintableKeys = lintable.map(normalize);
    setSkillLintStatus((current) => ({
      ...current,
      ...Object.fromEntries(lintableKeys.map((key) => [key, 'pending'])),
    }));

    const runBatches = async () => {
      for (
        let start = 0;
        start < lintable.length;
        start += SKILL_LINT_BATCH_SIZE
      ) {
        const batch = lintable.slice(start, start + SKILL_LINT_BATCH_SIZE);
        const batchKeys = batch.map(normalize);
        try {
          const results = await lint.mutateAsync(batch);
          const resultsByInput = byInput(results);
          setVerdictCache((cache) => ({ ...cache, ...resultsByInput }));
          setSkillLintStatus((current) => {
            const next = { ...current };
            batchKeys.forEach((key) => {
              if (resultsByInput[key]) delete next[key];
              else next[key] = 'failed';
            });
            return next;
          });
        } catch {
          // `failedSkills` carries these to the editor, which shows them inline with a retry.
          setSkillLintStatus((current) => ({
            ...current,
            ...Object.fromEntries(batchKeys.map((key) => [key, 'failed'])),
          }));
        }
      }
    };
    void runBatches();
  };

  // Lint-on-load: a loaded job's must-haves arrive without verdicts. Runs exactly once.
  const didInitialLint = useRef(false);
  useEffect(() => {
    if (didInitialLint.current || !enabled) return;
    didInitialLint.current = true;
    if (role.mustHave.length > 0) {
      queueMicrotask(() => lintSkills(role.mustHave));
    }
  });

  /** Lint the title on commit (blur); skips empty, cached and in-flight titles. */
  const lintTitle = (raw: string) => {
    const title = raw.trim();
    const key = normalize(title);
    if (
      !enabled ||
      !title ||
      titleVerdictCache[key] ||
      inFlightTitles.current.has(key)
    ) {
      return;
    }
    inFlightTitles.current.add(key);
    void titleLint
      .mutateAsync(title)
      .then((verdict) =>
        setTitleVerdictCache((cache) => ({ ...cache, [key]: verdict })),
      )
      .catch(() => toast.error("Couldn't check the title right now."))
      .finally(() => inFlightTitles.current.delete(key));
  };

  // Also lint the title on idle (not only on blur): the hint would otherwise appear on blur and shift the layout
  // under whatever the user is clicking next. Cache + in-flight guards keep this to one call per distinct title.
  const lintTitleRef = useRef(lintTitle);
  useEffect(() => {
    lintTitleRef.current = lintTitle;
  });
  useEffect(() => {
    const timer = window.setTimeout(
      () => lintTitleRef.current(role.title),
      TITLE_LINT_IDLE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [role.title]);

  return {
    verdictCache,
    verdicts,
    pendingCount,
    failedSkills,
    titleVerdict,
    needs,
    completeness: jobSignalQuery.data?.completeness,
    signalError: jobSignalQuery.isError,
    sufficiencyLevel,
    lintSkills,
    lintTitle,
  };
}
